import type { IdentificationData, PriceData } from "./types";

// Platform answers 200 with `data: null` — or a price whose `finalPrice` is absent —
// whenever the buyer returns no offer and whenever a provider yields no identification.
// The response types declare those payloads as always present, so callers dereferenced
// them blind. These guards keep that mismatch out of the type system: an incomplete
// payload leaves the client library as `null`, which the compiler then forces each
// caller to handle.

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * A price payload is usable only with both halves of `finalPrice` present; a partial
 * one renders as an empty or NaN amount rather than failing, which is worse than no price.
 */
export const isCompletePriceData = (value: unknown): value is PriceData => {
	if (!isRecord(value)) return false;
	const finalPrice = value.finalPrice;
	if (!isRecord(finalPrice)) return false;
	return (
		typeof finalPrice.value === "number" && Number.isFinite(finalPrice.value) && typeof finalPrice.currency === "string"
	);
};

/**
 * An identification payload is usable only with the nested record's id, which every
 * downstream call (capacity update, product selection, pricing) is keyed on.
 */
export const isCompleteIdentificationData = (value: unknown): value is IdentificationData => {
	if (!isRecord(value)) return false;
	const identification = value.identification;
	if (!isRecord(identification)) return false;
	return typeof identification.id === "string" && identification.id.length > 0;
};
